import {
  Button,
  GET_FORM_RESPONSE_STATUS,
  getDefaultActiveTableFilterOptions,
  LoadingSpinner,
  Table,
} from '@employ-engage/common-components';
import {
  ActiveTableFilterOption,
  BasicTableDataRow,
  TableCellContentAlignEnum,
  TableFilter,
  TableSortOption,
} from '@employ-engage/data';
import { useQuery } from '@apollo/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faCheck,
  faCircleInfo,
  faTriangleExclamation,
  faUserSecret,
} from '@fortawesome/pro-solid-svg-icons';

/* eslint-disable-next-line */
export interface FormResponseStatusPageProps {}

type Respondent = {
  id: string;
  employeeId: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  department: string[];
  dateCompleted: string | null;
  responseCount: number;
  deliveryStatus: 'delivered' | 'problem' | 'pending' | null;
};

const Stat = ({ label, value }: { label: string; value: string }) => (
  <div className="flex-1 bg-white rounded-2xl shadow px-6 py-5">
    <div className="text-neutral-500 text-sm font-medium">{label}</div>
    <div className="text-neutral-900 text-3xl font-semibold mt-1">{value}</div>
  </div>
);

const Note = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-start gap-3 bg-stone-50 ring-1 ring-gray-200 rounded-2xl px-5 py-4 text-sm text-neutral-600">
    <FontAwesomeIcon icon={faCircleInfo} className="mt-1 text-neutral-400" />
    <div>{children}</div>
  </div>
);

/**
 * One person's delivery state.
 *
 * "Delivered" means a mail server accepted the message. It does **not** mean
 * anyone read it — open tracking is deliberately off — so the wording says
 * delivered and never anything stronger.
 */
const DeliveryCell = ({
  outcome,
  label,
}: {
  outcome: Respondent['deliveryStatus'];
  label: (key: string) => string;
}) => {
  if (outcome === 'problem') {
    return (
      <span className="text-semantic-error font-medium">
        <FontAwesomeIcon icon={faTriangleExclamation} className="mr-2" />
        {label('Not delivered')}
      </span>
    );
  }
  if (outcome === 'delivered') {
    return <span className="text-neutral-600">{label('Delivered')}</span>;
  }
  if (outcome === 'pending') {
    return <span className="text-neutral-400">{label('Sending')}</span>;
  }
  // No record at all, which is not a failure — say nothing rather than
  // implying that something went wrong.
  return <span className="text-neutral-400">—</span>;
};

export function FormResponseStatusPage(props: FormResponseStatusPageProps) {
  const { t } = useTranslation(['common']);
  const { companyId, formId } = useParams();

  const { data: { formResponseStatus: status } = {}, loading } = useQuery(
    GET_FORM_RESPONSE_STATUS,
    { variables: { formId }, skip: !formId, fetchPolicy: 'network-only' }
  );

  const filters: TableFilter[] = [
    {
      id: 'responseStatus',
      value: t('Response'),
      options: [
        {
          key: 'responded',
          value: 'responded',
          label: t('Responded'),
          filter: (row: any) => row.hasResponded,
        },
        {
          key: 'not-responded',
          value: 'not-responded',
          label: t('Not Responded'),
          filter: (row: any) => !row.hasResponded,
        },
      ],
    },
  ] as TableFilter[];

  const [activeFilters, setActiveFilters] = useState<ActiveTableFilterOption[]>(
    getDefaultActiveTableFilterOptions(filters)
  );

  const sortOptions: TableSortOption[] = [
    {
      value: 'Name (A-Z)',
      sort: (row1, row2) =>
        `${row1.lastName} ${row1.firstName}`.localeCompare(
          `${row2.lastName} ${row2.firstName}`
        ),
    },
    {
      value: 'Name (Z-A)',
      sort: (row1, row2) =>
        `${row2.lastName} ${row2.firstName}`.localeCompare(
          `${row1.lastName} ${row1.firstName}`
        ),
    },
    {
      value: 'Responded (Most Recent)',
      // Nobody has a date until they respond, so outstanding people sort last
      // rather than scattering through the list as epoch-zero.
      sort: (row1, row2) =>
        (row2.dateCompleted ?? '').localeCompare(row1.dateCompleted ?? ''),
    },
    {
      value: 'Responded (Oldest)',
      sort: (row1, row2) =>
        (row1.dateCompleted || '9999').localeCompare(
          row2.dateCompleted || '9999'
        ),
    },
  ];

  const [activeSort, setActiveSort] = useState<TableSortOption>(sortOptions[0]);

  if (loading) {
    return <LoadingSpinner spinnerId="form-response-status-loading" />;
  }

  if (!status) {
    return (
      <div className="space-y-7">
        <Button to={`/${companyId}/admin/forms`} variant="link" size="small">
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          {t('Back to forms')}
        </Button>
        <Note>{t('This form could not be found.')}</Note>
      </div>
    );
  }

  const responseRate = status.invitedCount
    ? `${Math.round((status.respondedCount / status.invitedCount) * 100)}%`
    : '—';

  // One row per person, both lists together: the filter above narrows it, so
  // the counts in the header stay visible whichever side you are looking at.
  const rows = [
    ...status.responded.map((person: Respondent) => ({
      ...person,
      hasResponded: true,
    })),
    ...status.notResponded.map((person: Respondent) => ({
      ...person,
      hasResponded: false,
    })),
  ];

  const mapData = (row: any, index: number) => ({
    index,
    id: row.id,
    lastName: row.lastName,
    firstName: row.firstName,
    dateCompleted: row.dateCompleted,
    hasResponded: row.hasResponded,
    name: `${row.firstName} ${row.lastName}`.trim() || row.employeeId,
    emailAddress: row.emailAddress,
    department: row.department?.join(' › ') || '—',
    status: row.hasResponded ? (
      <span className="text-green-600 font-medium">
        <FontAwesomeIcon icon={faCheck} className="mr-2" />
        {t('Responded')}
        {row.responseCount > 1 ? ` (${row.responseCount})` : ''}
      </span>
    ) : (
      <span className="text-neutral-400">{t('Not yet')}</span>
    ),
    completedOn: row.dateCompleted
      ? t('dateShort', { date: new Date(row.dateCompleted) })
      : '—',
    delivery: <DeliveryCell outcome={row.deliveryStatus} label={t} />,
  });

  return (
    <div className="space-y-7 flex flex-col">
      <div>
        <Button to={`/${companyId}/admin/forms`} variant="link" size="small">
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          {t('Back to forms')}
        </Button>
      </div>

      <div className="w-full pl-9 pr-[35.92px] pt-[37.91px] pb-[36.99px] bg-gradient-to-l from-light-primary to-light-primary rounded-lg">
        <div className="text-white text-2xl font-bold">{status.formTitle}</div>
        <div className="text-gray-200 text-base font-normal mt-2">
          {t('Who has responded to this form.')}
        </div>
      </div>

      <div className="flex gap-5">
        <Stat label={t('Invited')} value={String(status.invitedCount)} />
        <Stat
          label={status.isAnonymous ? t('Responses') : t('Responded')}
          value={String(status.respondedCount)}
        />
        <Stat label={t('Response rate')} value={responseRate} />
      </div>

      {/**
        * The headline rate is left as it was — invited over responded — and
        * the caveat is put beneath it rather than folded into the number. A
        * rate that quietly changed its denominator would not match any other
        * report of the same survey.
        */}
      {status.deliveryTracked &&
        (status.deliveryCounts?.problem ?? 0) > 0 && (
          <div className="text-sm text-neutral-500 -mt-3">
            {t('response-rate-reachable', {
              count: status.deliveryCounts.problem,
              defaultValue:
                'The rate above counts everyone invited, including {{count}} whose email did not arrive.',
            })}
          </div>
        )}

      {status.namesWithheld && (
        <div className="flex items-start gap-3 bg-orange-50 ring-1 ring-orange-200 rounded-2xl px-5 py-4 text-sm text-neutral-700">
          <FontAwesomeIcon
            icon={faUserSecret}
            className="mt-1 text-light-secondaryAccent"
          />
          <div>
            <div className="font-medium">
              {t('This form is anonymous for all respondents.')}
            </div>
            <div className="mt-1">
              {t(
                'No identity is recorded against an anonymous response, so who answered cannot be shown — only how many did.'
              )}
              {status.allowsMultipleResponses
                ? ` ${t(
                    'This form also allows more than one response per person, so the response count may be higher than the number of people who answered.'
                  )}`
                : ''}
            </div>
          </div>
        </div>
      )}

      {/**
        * Shown whether or not names are withheld: on an anonymous form this is
        * the only delivery information there is, and it is the reason the
        * response rate is lower than it looks.
        *
        * The figure is combined on purpose. Bounced, complained and failed
        * broken out would each be a smaller cell, and on an anonymous survey
        * the difference between two of them is a fact about one identifiable
        * person.
        */}
      {(status.deliveryCounts?.problem ?? 0) > 0 && (
        <div className="flex items-start gap-3 bg-red-50 ring-1 ring-red-200 rounded-2xl px-5 py-4 text-sm text-neutral-700">
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            className="mt-1 text-semantic-error"
          />
          <div>
            <div className="font-medium">
              {t('emails-not-delivered', {
                count: status.deliveryCounts.problem,
                defaultValue:
                  '{{count}} invitation(s) could not be delivered.',
              })}
            </div>
            <div className="mt-1">
              {status.namesWithheld
                ? t(
                    'Those people never received the survey, so they are counted as invited but could not have answered. Bounced addresses are flagged on the employee list.'
                  )
                : t(
                    'Those people never received the survey, so they are counted as invited but could not have answered. The addresses are flagged below and on the employee list.'
                  )}
            </div>
          </div>
        </div>
      )}

      {status.targetGroupCount > 1 && (
        <Note>
          {t(
            'This form has more than one target group. The invited figure counts everyone matched by any group, which can differ slightly from who received the email.'
          )}
        </Note>
      )}

      {status.unattributedCount > 0 && (
        <Note>
          {t('completed-responses-unattributed', {
            count: status.unattributedCount,
            defaultValue:
              '{{count}} completed response(s) belong to someone who is no longer on the employee list, so they are counted above but not named below.',
          })}
        </Note>
      )}

      {!status.namesWithheld && (
        // Filtering and sorting happen in memory over the whole list — fine at
        // a few hundred employees, which is what this table is built for.
        <Table
          label={t('People')}
          sortOptions={sortOptions.map((option) => ({
            ...option,
            value: t(option.value),
          }))}
          activeSort={{ ...activeSort, value: t(activeSort.value) }}
          setActiveSort={setActiveSort}
          filters={filters}
          activeFilters={activeFilters}
          setActiveFilters={setActiveFilters}
          headerList={[
            { id: 'name', headerLabel: t('Name') },
            { id: 'emailAddress', headerLabel: t('Email') },
            { id: 'department', headerLabel: t('Department') },
            { id: 'status', headerLabel: t('Status') },
            // Hidden entirely on a survey sent before any of this existed.
            // An empty column would read as "nothing was delivered".
            ...(status.deliveryTracked
              ? [{ id: 'delivery', headerLabel: t('Delivery') }]
              : []),
            {
              id: 'completedOn',
              headerLabel: t('Completed'),
              alignment: TableCellContentAlignEnum.CENTER,
            },
          ]}
          dataList={rows as unknown as BasicTableDataRow[]}
          mapData={mapData}
        />
      )}
    </div>
  );
}

export default FormResponseStatusPage;
